import { Pagination, Skeleton } from '@vtex/shoreline'

import { AlertWithTitle } from '~/components/AlertWithTitle'
import { handleErrorMessage } from '~/helpers/errors'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import styles from './styles.module.css'

/**
 * Cartão das listagens do catálogo: skeleton, empty state (vazio ou filtrado),
 * tabela e rodapé com contagem e paginação. Com erro na busca, só o alerta —
 * lista vazia e "nada cadastrado" não podem se confundir com falha de API.
 */
export const CatalogListCard: React.FC<{
  isPending: boolean
  isEmpty: boolean
  /** Erro da query; `Error` é o que o TanStack v5 entrega. */
  error?: Error | null
  /** Há busca ou filtro ativo: o vazio é "nada encontrado", não "nada cadastrado". */
  hasFilters: boolean
  empty: React.ReactNode
  emptyFiltered: React.ReactNode
  countLabel: string
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  children: React.ReactNode
}> = ({
  isPending,
  isEmpty,
  error,
  hasFilters,
  empty,
  emptyFiltered,
  countLabel,
  page,
  pageSize,
  total,
  onPageChange,
  children
}) => {
  const { t } = useAPPTranslation()

  if (error) {
    return (
      <div className={styles.card}>
        <AlertWithTitle
          title={t('rm:somethingWentWrong.alert')}
          description={handleErrorMessage({
            error,
            fallbackMessage: t('c:messages.errorOccurred.message')
          })}
        />
      </div>
    )
  }

  return (
    <div className={styles.card}>
      {isPending && <Skeleton style={{ height: '12rem' }} />}

      {!isPending && isEmpty && (hasFilters ? emptyFiltered : empty)}

      {!isPending && !isEmpty && (
        <>
          {children}

          <div className={styles.cardFooter}>
            <span className={styles.count}>{countLabel}</span>
            <Pagination
              page={page}
              size={pageSize}
              total={total}
              onPageChange={onPageChange}
            />
          </div>
        </>
      )}
    </div>
  )
}
